import { prismicClient } from '@/data';
import { usePrismicStaleTime } from '@/data/prismicHooks';
import { useActiveProductConfig } from '@/hooks/use-active-product';
import { PickTypesEducationalDocument, PickTypesEducationalDocumentDataPickTypesItem } from '@/types/prismic.generated';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';

type Props = ['pick_types_educational', string | undefined];

export type PickTypeEducationItem = PickTypesEducationalDocumentDataPickTypesItem;

const fetcher = async (context: QueryFunctionContext<Props>) => {
    const [key, pickTypesEducationalUid] = context.queryKey;
    const document = await prismicClient.getByUID<PickTypesEducationalDocument>(key, pickTypesEducationalUid ?? '');

    // only surface pick types flagged as displayed in Prismic
    const pickTypes: PickTypeEducationItem[] = (document.data.pick_types ?? []).filter(item => item.displayed);

    return { ...document.data, pickTypes };
};

/**
 * Fetches the Pick Types Educational content from Prismic for the active product.
 *
 * This content rarely changes, so the query is cached via React Query and only
 * refetched after the Prismic stale time elapses (configurable in Prismic, defaults to 15m).
 */
export const usePickTypesEducationData = () => {
    const productConfig = useActiveProductConfig();
    const pickTypesEducationalUid = productConfig?.pickTypesEducationalUid;
    const staleTime = usePrismicStaleTime();

    return useQuery({
        queryKey: ['pick_types_educational', pickTypesEducationalUid],
        queryFn: fetcher,
        staleTime,
        enabled: !!pickTypesEducationalUid,
    });
};
